import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUserId } from "@/hooks/use-current-user";
import { useProjectId } from "@/hooks/use-project-id";

export type MyCourse = {
  id: string;
  title: string;
  category: string;
  cover_image: string | null;
  progress: number;
  completed: boolean;
};

/**
 * Returns the signed-in user's enrolled courses with progress.
 * Reuses the same `course_enrollments` + `courses` join used by the
 * web My Progress / Courses pages so mobile + web stay in sync.
 */
export function useMyCourses() {
  const { data: userId } = useCurrentUserId();
  const { data: projectId } = useProjectId();

  return useQuery({
    queryKey: ["my-courses-mobile", userId, projectId],
    enabled: !!userId && !!projectId,
    staleTime: 2 * 60_000,
    queryFn: async (): Promise<MyCourse[]> => {
      if (!userId || !projectId) return [];
      const { data, error } = await supabase
        .from("course_enrollments")
        .select(
          `course_id, progress, completed,
           courses!inner ( id, title, category, cover_image, status, archived )`
        )
        .eq("user_id", userId)
        .eq("project_id", projectId);
      if (error) throw error;
      // Normalise category once here so every downstream mobile consumer
      // (filter chips, sorting via localeCompare, card letter tile, label helper)
      // can rely on a guaranteed non-empty string. The DB column is multi-value
      // and can arrive as an array, a string, null, or undefined.
      const normaliseCategory = (raw: unknown): string => {
        if (Array.isArray(raw)) {
          const joined = raw
            .filter((v) => v != null && String(v).trim() !== "")
            .map((v) => String(v).trim())
            .join(",");
          return joined || "General";
        }
        if (raw == null) return "General";
        const s = String(raw).trim();
        return s || "General";
      };

      return (data || [])
        .map((row: any) => {
          const c = row.courses;
          if (!c || c.archived || c.status !== "active") return null;
          return {
            id: c.id,
            title: c.title,
            category: normaliseCategory(c.category),
            cover_image: c.cover_image || null,
            progress: row.progress || 0,
            completed: !!row.completed,
          } as MyCourse;
        })
        .filter(Boolean) as MyCourse[];
    },
  });
}
